import { useMemo } from 'react';
import { clsx } from 'clsx';
import { Check, AlertTriangle } from 'lucide-react';
import { WIZARD_STEPS, STEP_LABELS, isStepComplete, WizardStep } from '../../types/configurator';
import { useConfigurator } from '../../hooks/useConfigurator';

export function StepIndicator() {
  const { currentStep, config, goToStep, validate } = useConfigurator();
  const currentIndex = WIZARD_STEPS.indexOf(currentStep);
  const totalSteps = WIZARD_STEPS.length;
  const progressPercent = (currentIndex / (totalSteps - 1)) * 100;

  // Memoize validation to avoid recalculating on every render
  const validationErrors = useMemo(() => validate(), [config]);

  // Check for warnings in each step
  const getStepWarnings = (step: WizardStep): boolean => {
    const stepWarnings: Record<WizardStep, string[]> = {
      scenario: [],
      network: ['network'],
      nodes: ['nodes', 'node'],
      services: ['service'],
      security: ['security', 'vpn', 'peer'],
      summary: [],
    };
    const keywords = stepWarnings[step];
    return validationErrors.some(
      e => e.severity === 'warning' && keywords.some(k => e.field?.includes(k) || e.message.toLowerCase().includes(k))
    );
  };

  return (
    <nav aria-label="Progress" className="mb-6 sm:mb-8">
      {/* Progress bar with step markers */}
      <div className="relative flex items-center justify-between">
        {/* Background track */}
        <div className="absolute left-0 right-0 h-1 bg-gray-200 rounded-full" style={{ top: '50%', transform: 'translateY(-50%)' }} />

        {/* Filled progress */}
        <div
          className="absolute left-0 h-1 bg-hermit-600 rounded-full transition-all duration-300 ease-out"
          style={{ top: '50%', transform: 'translateY(-50%)', width: `${progressPercent}%` }}
        />

        {/* Step markers */}
        {WIZARD_STEPS.map((step, index) => {
          const isComplete = isStepComplete(step, config) && index < currentIndex;
          const isCurrent = step === currentStep;
          const isPast = index < currentIndex;
          const canNavigate = isPast || isComplete;
          const hasWarning = isPast && getStepWarnings(step);

          return (
            <button
              key={step}
              onClick={() => canNavigate && goToStep(step)}
              disabled={!canNavigate}
              className={clsx(
                'relative z-10 flex items-center justify-center rounded-full transition-all duration-200',
                'w-8 h-8 sm:w-10 sm:h-10',
                isCurrent && 'bg-hermit-600 text-white ring-4 ring-hermit-100 scale-110',
                isPast && !hasWarning && 'bg-hermit-600 text-white',
                isPast && hasWarning && 'bg-amber-500 text-white',
                !isCurrent && !isPast && 'bg-white border-2 border-gray-300 text-gray-400',
                canNavigate && !isCurrent && 'cursor-pointer hover:scale-105 hover:ring-2 hover:ring-hermit-200',
                !canNavigate && 'cursor-default'
              )}
              title={STEP_LABELS[step]}
            >
              {isPast && hasWarning ? (
                <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5" />
              ) : isPast ? (
                <Check className="w-4 h-4 sm:w-5 sm:h-5" />
              ) : (
                <span className="text-sm sm:text-base font-semibold">{index + 1}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Step labels */}
      <ol className="flex justify-between mt-3">
        {WIZARD_STEPS.map((step, index) => {
          const isCurrent = step === currentStep;
          const isPast = index < currentIndex;
          const hasWarning = isPast && getStepWarnings(step);

          return (
            <li
              key={step}
              className={clsx(
                'text-xs sm:text-sm font-medium transition-colors flex-1',
                index === 0 && 'text-left',
                index === totalSteps - 1 && 'text-right',
                index > 0 && index < totalSteps - 1 && 'text-center',
                isCurrent && 'text-hermit-700 font-semibold',
                isPast && !hasWarning && 'text-hermit-600',
                isPast && hasWarning && 'text-amber-600',
                !isCurrent && !isPast && 'text-gray-400'
              )}
            >
              <span className="hidden sm:inline">{STEP_LABELS[step]}</span>
              <span className="sm:hidden">
                {isCurrent ? STEP_LABELS[step].slice(0, 4) : ''}
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
